import { Component } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

//ensure it works with default html img properties
type ImageWrapperProps = {
  id?: string;
  src: string;
  previewSrc?: string; // Downsized preview URL (e.g. server proxy). Falls back to src if load fails.
  fileList?: string[];  // List of image URLs for gallery navigation
  className?: string;
  alt?: string;
  width?: number | null;  // Known image width for placeholder sizing
  height?: number | null; // Known image height for placeholder sizing
  onUseImageAttachment?: () => void;
}

function isTouchDevice() {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
}

@observer
export class ImageWrapper extends Component<ImageWrapperProps> {
  @observable isFullscreen = false;
  @observable isLoading = false;
  @observable imageError = false;
  @observable previewFailed = false; // true when previewSrc load failed, falls back to src
  @observable zoomScale = 1;
  @observable translateX = 0;
  @observable translateY = 0;
  @observable currentImageIndex = 0;
  @observable isTransitioningImage = false;
  @observable showNavButtons = false;
  @observable isLoadingFullscreen = false;
  @observable nextImageSrc: string | null = null;
  @observable isDownloading = false;

  private initialDistance = 0;
  private initialScale = 1;
  private imageRect: DOMRect | null = null;
  private isPinching = false;
  private isTransitioning = false;
  private transformOrigin = 'center center';
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;

  @action
  openFullscreen = () => {
    const { src, fileList } = this.props;
    if (fileList && fileList.length > 0) {
      const idx = fileList.indexOf(src);
      this.currentImageIndex = idx !== -1 ? idx : 0;
    }
    this.isFullscreen = true;
    this.isLoadingFullscreen = true;
    document.body.style.overflow = 'hidden';
  };

  @action
  closeFullscreen = () => {
    this.isFullscreen = false;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.currentImageIndex = 0;
    document.body.style.overflow = 'unset';
  };

  @action
  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  @action
  setImageError = (error: boolean) => {
    this.imageError = error;
  };

  @action
  setZoom = (scale: number, x: number = 0, y: number = 0) => {
    this.zoomScale = scale;
    this.translateX = x;
    this.translateY = y;
  };

  @action
  resetZoom = () => {
    this.isTransitioning = true;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  centerZoom = () => {
    this.isTransitioning = true;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  navigateImage = (direction: 'prev' | 'next') => {
    const { fileList } = this.props;
    if (!fileList || fileList.length <= 1) return;

    // Reset zoom immediately without transition
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isTransitioning = false;

    const nextIndex = direction === 'next'
      ? (this.currentImageIndex + 1) % fileList.length
      : (this.currentImageIndex - 1 + fileList.length) % fileList.length;

    const nextSrc = fileList[nextIndex];
    this.nextImageSrc = nextSrc;
    this.isLoadingFullscreen = true;

    const img = new Image();
    img.onload = () => {
      this.isLoadingFullscreen = false;
      this.isTransitioningImage = true;
      setTimeout(() => {
        this.currentImageIndex = nextIndex;
        this.isTransitioningImage = false;
        requestAnimationFrame(() => { this.nextImageSrc = null; });
      }, 300);
    };
    img.onerror = () => {
      this.isLoadingFullscreen = false;
      this.isTransitioningImage = true;
      setTimeout(() => {
        this.currentImageIndex = nextIndex;
        this.isTransitioningImage = false;
        requestAnimationFrame(() => { this.nextImageSrc = null; });
      }, 300);
    };
    img.src = nextSrc;
  };

  @action
  setShowNavButtons = (show: boolean) => {
    this.showNavButtons = show;
  };

  handleImageLoad = () => {
    this.setLoading(false);
    this.setImageError(false);
  };

  @action
  handleImageError = () => {
    const { previewSrc } = this.props;
    if (previewSrc && !this.previewFailed) {
      // Preview proxy failed — fall back to the original src
      this.previewFailed = true;
      return;
    }
    this.setLoading(false);
    this.setImageError(true);
  };

  @action
  handleFullscreenImageLoad = () => {
    this.isLoadingFullscreen = false;
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (!this.isFullscreen) return;
    if (e.key === 'Escape') {
      this.closeFullscreen();
    } else if (e.key === 'ArrowLeft') {
      this.navigateImage('prev');
    } else if (e.key === 'ArrowRight') {
      this.navigateImage('next');
    }
  };

  getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.isSwiping = false;
      this.initialDistance = this.getTouchDistance(e.touches);
      this.initialScale = this.zoomScale;
      this.imageRect = e.currentTarget.getBoundingClientRect();
      const touchCenter = this.getTouchCenter(e.touches);
      const originX = touchCenter.x - this.imageRect.left;
      const originY = touchCenter.y - this.imageRect.top;
      this.transformOrigin = `${originX}px ${originY}px`;
    } else if (e.touches.length === 1) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = Date.now();
      this.isSwiping = true;
    }
  };

  handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = this.getTouchDistance(e.touches);
      let scale = (currentDistance / this.initialDistance) * this.initialScale;
      scale = Math.max(1, Math.min(scale, 5));
      this.setZoom(scale);
    } else if (this.isSwiping && e.touches.length === 1) {
      const deltaX = Math.abs(e.touches[0].clientX - this.touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  handleTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isPinching) {
      this.isPinching = false;
      this.resetZoom();
      setTimeout(() => { this.transformOrigin = 'center center'; }, 300);
    } else if (this.isSwiping) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;
      const deltaTime = Date.now() - this.touchStartTime;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / deltaTime;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && velocity > 0.3) {
        const { fileList } = this.props;
        if (fileList && fileList.length > 1) {
          this.navigateImage(deltaX > 0 ? 'prev' : 'next');
        }
      }
      this.isSwiping = false;
    }
  };

  handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    const { fileList } = this.props;
    if (fileList && fileList.length > 1) {
      this.navigateImage('next');
    }
  };

  getCurrentImageSrc = () => {
    const { fileList, src } = this.props;
    if (fileList && fileList.length > 0) {
      return fileList[this.currentImageIndex];
    }
    return src;
  };

  @action.bound
  downloadImage = async () => {
    const imageUrl = this.getCurrentImageSrc();
    this.isDownloading = true;
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(`Network response is ${response.status}: ` + response.statusText);
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `image-${Date.now()}.jpg`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      this.isDownloading = false;
    }
  };

  constructor(props: ImageWrapperProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'unset';
  }

  render() {
    const { src, previewSrc, className = '', alt = 'Image', id, width, height, onUseImageAttachment, fileList } = this.props;
    const hasGallery = fileList && fileList.length > 1;
    const currentSrc = this.getCurrentImageSrc();
    // Use server preview for the thumbnail, fall back to original on error
    const thumbnailSrc = previewSrc && !this.previewFailed ? previewSrc : src;

    const thumbnailStyle: React.CSSProperties = {
      cursor: 'pointer',
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease'
    };

    // Use known dimensions for placeholder sizing to prevent layout shift
    if (width && height) {
      thumbnailStyle.aspectRatio = `${width} / ${height}`;
      thumbnailStyle.maxWidth = Math.min(width, 512) + 'px';
      thumbnailStyle.width = width;
      thumbnailStyle.height = height;
      const aspectRatio = width / height;
      if (aspectRatio > 1) {
        thumbnailStyle.width = '300px';
        thumbnailStyle.height = `${300 / aspectRatio}px`;
      } else {
        thumbnailStyle.height = '300px';
        thumbnailStyle.width = `${300 * aspectRatio}px`;
      }
    }

    return (
      <>
        {/* Thumbnail Image */}
        <span className={`image-wrapper ${className}`} style={{ position: 'relative', display: 'inline-block' }}>
          <img
            id={id}
            src={thumbnailSrc}
            alt={alt}
            loading='lazy'
            className="thumbnail-image"
            onClick={this.openFullscreen}
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
            style={thumbnailStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
              e.currentTarget.style.filter = 'brightness(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.filter = 'none';
            }}
          />
          {onUseImageAttachment && (
            <button
              className="attachment-use-as-ref-btn"
              title="Use as reference image"
              onClick={(e) => { e.stopPropagation(); onUseImageAttachment(); }}
            >
              &#x2197;
            </button>
          )}
        </span>

        {/* Fullscreen Modal */}
        {this.isFullscreen && (
          <span
            className="fullscreen-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) this.closeFullscreen();
            }}
            onMouseMove={() => { if (hasGallery) this.setShowNavButtons(true); }}
            onMouseLeave={() => { if (hasGallery) this.setShowNavButtons(false); }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.92)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              animation: 'fadeIn 0.25s ease',
              overflow: 'hidden'
            }}
          >
            {/* Blurred ambient background */}
            <img
              src={currentSrc}
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(40px)',
                opacity: 0.18,
                transform: 'scale(1.1)',
                pointerEvents: 'none',
                zIndex: 0
              }}
            />

            {/* Controls Bar */}
            <span
              className="fullscreen-controls"
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                display: 'flex',
                gap: '12px',
                zIndex: 2001
              }}
            >
              <button
                onClick={this.downloadImage}
                className="control-button"
                title="Download Image"
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'white',
                  cursor: this.isDownloading ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '18px',
                  width: '48px',
                  height: '48px',
                  opacity: this.isDownloading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!this.isDownloading) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.22)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {this.isDownloading ? (
                  <span style={{
                    width: '20px', height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    display: 'inline-block'
                  }} />
                ) : '⬇'}
              </button>
              <button
                onClick={this.closeFullscreen}
                className="control-button"
                title="Close (ESC)"
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '20px',
                  width: '48px',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(220,50,50,0.6)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ✕
              </button>
            </span>

            {/* Gallery Counter */}
            {hasGallery && (
              <span
                style={{
                  position: 'fixed',
                  top: '20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  zIndex: 2001
                }}
              >
                {this.currentImageIndex + 1} / {fileList.length}
              </span>
            )}

            {/* Navigation Buttons */}
            {hasGallery && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); this.navigateImage('prev'); }}
                  style={{
                    position: 'fixed',
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    zIndex: 2001,
                    opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                    transition: 'all 0.3s ease',
                    pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); this.navigateImage('next'); }}
                  style={{
                    position: 'fixed',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    zIndex: 2001,
                    opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                    transition: 'all 0.3s ease',
                    pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  ›
                </button>
              </>
            )}

            {/* Full Size Image Container with Cross-fade */}
            <span
              className="fullscreen-image-container"
              style={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                position: 'relative',
                zIndex: 1
              }}
            >
              {/* Loading Spinner */}
              {this.isLoadingFullscreen && (
                <span style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2002,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{
                    width: '48px', height: '48px',
                    border: '4px solid rgba(255,255,255,0.2)',
                    borderTop: '4px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block'
                  }} />
                </span>
              )}

              {/* Next Image (bottom layer for cross-fade) */}
              {this.nextImageSrc && (
                <img
                  src={this.nextImageSrc}
                  alt={alt}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 8px 48px rgba(0, 0, 0, 0.7)',
                    opacity: this.isTransitioningImage ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                    cursor: hasGallery ? 'pointer' : 'default'
                  }}
                />
              )}

              {/* Current Image (top layer) */}
              <img
                src={currentSrc}
                alt={alt}
                onLoad={this.handleFullscreenImageLoad}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 8px 48px rgba(0, 0, 0, 0.7)',
                  animation: 'zoomIn 0.3s ease',
                  transform: `scale(${this.zoomScale})`,
                  transformOrigin: this.transformOrigin,
                  transition: this.isTransitioningImage ? 'opacity 0.3s ease' : this.isTransitioning ? 'transform 0.3s ease' : 'none',
                  opacity: this.isTransitioningImage ? 0 : 1,
                  position: 'relative',
                  zIndex: 2,
                  cursor: hasGallery ? 'pointer' : 'default'
                }}
                onClick={hasGallery ? this.handleImageClick : (e) => e.stopPropagation()}
                onTouchStart={this.handleTouchStart}
                onTouchMove={this.handleTouchMove}
                onTouchEnd={this.handleTouchEnd}
              />
            </span>

            {/* Bottom hint */}
            <span
              style={{
                position: 'fixed',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '13px',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 2001
              }}
            >
              {!isTouchDevice()
                ? hasGallery
                  ? 'Click image or use arrow keys to navigate • Press ESC to close'
                  : 'Tap outside image or press ESC to close'
                : hasGallery
                  ? 'Tap image or swipe to navigate • Tap outside to close'
                  : 'Tap outside image to close'
              }
            </span>
          </span>
        )}

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes zoomIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }
}